<script setup lang="ts">
/**
 * Full-screen mobile overlay for a group detail view.
 */
import { computed, ref, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { deleteGroup, fetchGroup, isGroupActive } from '@/api/groups'
import { groupsPollingInterval } from '@/config/api'
import { useAddGroupFormStore } from '@/stores/add_group_form_store'
import { useGroupDisplay } from '@/composables/useGroupDisplay'
import { useAllPerformers } from '@/composables/useAllPerformers'
import { useAllBusinessServiceTypes } from '@/composables/useAllBusinessServiceTypes'
import { useAllBookingTypes } from '@/composables/useAllBookingTypes'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'

const props = defineProps<{
  groupId: string
}>()

const emit = defineEmits<{
  close: []
}>()

const groupId = toRef(props, 'groupId')
const { t } = useI18n()
const { hasRole } = usePermissions()
const canEdit = computed(() => hasRole(ResourceAccessRole.GroupsEdit))
const canDelete = computed(() => hasRole(ResourceAccessRole.GroupsDelete))
const { displayName, scheduleLines } = useGroupDisplay()

const groupQuery = useQuery({
  queryKey: ['group', groupId],
  queryFn: () => fetchGroup(groupId.value),
  staleTime: groupsPollingInterval,
  refetchInterval: groupsPollingInterval,
  refetchIntervalInBackground: false,
})
const group = computed(() => groupQuery.data.value ?? null)

// Each lookup below hits a catalog endpoint gated on a role the Groups
// pane itself doesn't require (reachable via ClientsView/GroupsView
// alone, e.g. Finance) — disabled when absent so a 403 doesn't trip the
// shared axios interceptor's app-wide navigation; see GroupCard.vue's
// identical comment.
const canViewBookingTypes = computed(() => hasRole(ResourceAccessRole.BookingTypesView))
const canViewPerformers = computed(() => hasRole(ResourceAccessRole.StuffView))
const canViewServiceTypes = computed(() => hasRole(ResourceAccessRole.BusinessServiceTypesView))

const { bookingTypes } = useAllBookingTypes({ enabled: canViewBookingTypes })
const room = computed<string | null>(() => {
  const g = group.value
  if (!g || !g.has_dedicated_schedule || g.booking_type_id === null) return null
  return bookingTypes.value.find((bt) => bt.id === g.booking_type_id)?.address ?? null
})

const { performers: allPerformers } = useAllPerformers({ enabled: canViewPerformers })
function genderLetter(raw: string): 'm' | 'f' | string {
  const u = raw.trim().toUpperCase()
  if (u === 'FEMALE' || u === 'F') return 'f'
  if (u === 'MALE' || u === 'M') return 'm'
  return u.length > 0 ? u[0]!.toLowerCase() : '–'
}
const performers = computed(() => {
  const g = group.value
  if (!g) return []
  const byId = new Map(allPerformers.value.map((p) => [p.id, p]))
  return g.performer_ids
    .map((id) => byId.get(id))
    .filter((p): p is NonNullable<typeof p> => p !== undefined)
    .map((p) => ({ id: p.id, name: p.name, gender: genderLetter(p.gender) }))
})

const { businessServiceTypes: allTypes } = useAllBusinessServiceTypes({
  enabled: canViewServiceTypes,
})
const serviceTypeLabels = computed(() => {
  const g = group.value
  if (!g) return []
  const byId = new Map(allTypes.value.map((type) => [type.id, type]))
  return g.business_service_type_id
    ? [byId.get(g.business_service_type_id)?.description].filter(
        (label): label is string => !!label && label.trim() !== '',
      )
    : []
})

const active = computed(() => (group.value ? isGroupActive(group.value) : true))
const slotLines = computed(() => scheduleLines(group.value?.schedule))

// ── Edit ──────────────────────────────────────────────────────────────────────
const addGroupFormStore = useAddGroupFormStore()

function openEdit(): void {
  const g = group.value
  if (!g) return
  addGroupFormStore.openForEdit(g.id, {
    name: g.name,
    description: g.description,
    start: g.start,
    end: g.end,
    hasDedicatedSchedule: g.has_dedicated_schedule,
    bookingTypeId: g.booking_type_id,
    businessServiceTypeId: g.business_service_type_id,
    performerIds: g.performer_ids ?? [],
    schedule: g.schedule ?? [],
  })
}

// ── Delete ────────────────────────────────────────────────────────────────────
const queryClient = useQueryClient()
const showDeleteConfirm = ref(false)

const deleteMutation = useMutation({
  mutationFn: () => deleteGroup(groupId.value),
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['groups'] })
    showDeleteConfirm.value = false
    emit('close')
  },
})

const deleteDescription = computed<string>(() => {
  const name = group.value ? displayName(group.value) : t('cards.group.defaultName')
  return t('cards.group.confirmDeleteDescription', { name })
})

function handleDelete(): void {
  if (!group.value) return
  showDeleteConfirm.value = true
}
function confirmDelete(): void {
  deleteMutation.mutate()
}
function cancelDelete(): void {
  showDeleteConfirm.value = false
}
</script>

<template>
  <Teleport to="body">
    <div class="gm-screen font-tufte text-tufte-text">
      <nav class="gm-navbar">
        <button type="button" class="gm-nav-back" @click="emit('close')">
          <svg
            width="8"
            height="14"
            viewBox="0 0 8 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 1L1 7L7 13" />
          </svg>
          {{ t('tabs.clients.groupsTab') }}
        </button>
        <span class="gm-nav-title">{{ t('cards.group.navTitle') }}</span>
        <button
          v-if="canEdit"
          type="button"
          class="gm-nav-edit"
          :disabled="!group"
          @click="openEdit"
        >
          {{ t('common.edit') }}
        </button>
      </nav>

      <div v-if="!group && groupQuery.isPending.value" class="gm-state">
        {{ t('common.loadingShort') }}
      </div>
      <div v-else-if="!group && groupQuery.isError.value" class="gm-state gm-state--error">
        {{ t('cards.group.loadError') }}
      </div>

      <div v-else-if="group" class="gm-scroll">
        <div class="gm-header">
          <div class="gm-name">{{ displayName(group) }}</div>
          <span :class="active ? 'gm-state-on' : 'gm-state-off'">{{
            active ? t('cards.group.stateActive') : t('cards.group.stateInactive')
          }}</span>
        </div>

        <div v-if="group.description" class="gm-description">{{ group.description }}</div>

        <div class="gm-section-label">{{ t('cards.group.scheduleLabel') }}</div>
        <div class="gm-group">
          <div v-if="slotLines.length" class="gm-schedule">
            <span v-for="(line, i) in slotLines" :key="i" class="gm-sl">{{ line }}</span>
          </div>
          <div v-else class="gm-empty-note">{{ t('cards.group.noSchedule') }}</div>
        </div>

        <div class="gm-section-label">{{ t('cards.group.roomLabelShort') }}</div>
        <div class="gm-group">
          <span v-if="room">{{ room }}</span>
          <span v-else class="gm-empty-note">{{ t('cards.group.roomNotSet') }}</span>
        </div>

        <div class="gm-section-label">{{ t('cards.group.performersLabel') }}</div>
        <div class="gm-group">
          <div v-if="performers.length" class="gm-performers">
            <span
              v-for="p in performers"
              :key="p.id"
              :class="p.gender === 'm' ? 'gm-perf--m' : 'gm-perf--f'"
            >
              {{ p.name }}
            </span>
          </div>
          <div v-else class="gm-empty-note">{{ t('cards.group.performersNotSet') }}</div>
        </div>

        <div class="gm-section-label">{{ t('cards.group.serviceTypesLabel') }}</div>
        <div class="gm-group">
          <div v-if="serviceTypeLabels.length" class="gm-svcs">
            <span v-for="label in serviceTypeLabels" :key="label" class="gm-svc">{{ label }}</span>
          </div>
          <div v-else class="gm-empty-note">–</div>
        </div>

        <div class="gm-fill" />

        <div v-if="canDelete" class="gm-danger-group">
          <button
            type="button"
            class="gm-danger-row"
            :disabled="deleteMutation.isPending.value"
            @click="handleDelete"
          >
            {{ deleteMutation.isPending.value ? t('common.deleting') : t('common.delete') }}
          </button>
        </div>

        <div class="gm-scroll-end" />
      </div>

      <DeleteConfirmSheet
        :open="showDeleteConfirm"
        :title="t('cards.group.confirmDeleteTitle')"
        :description="deleteDescription"
        :pending="deleteMutation.isPending.value"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </div>
  </Teleport>
</template>

<style scoped>
.gm-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: #f4f3ea;
  display: flex;
  flex-direction: column;
}

.gm-navbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.gm-nav-back {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  justify-content: flex-start;
}
.gm-nav-back:active {
  opacity: 0.6;
}

.gm-nav-title {
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  flex: 1;
  text-align: center;
}

.gm-nav-edit {
  font: inherit;
  font-size: 0.9rem;
  text-transform: uppercase;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  text-align: right;
}
.gm-nav-edit:disabled {
  color: var(--color-tufte-muted);
  cursor: default;
}
.gm-nav-edit:not(:disabled):active {
  opacity: 0.6;
}

.gm-state {
  padding: 1.5rem 1.25rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.9rem;
  color: var(--color-tufte-muted);
}
.gm-state--error {
  color: var(--color-money-negative);
}

.gm-scroll {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.gm-header {
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 1.3rem 1.25rem 1.2rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.7rem;
}

.gm-name {
  font-size: 1.9rem;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.01em;
}

.gm-state-on {
  font-family: var(--font-tufte-sans);
  font-size: 0.8rem;
  color: var(--color-tufte-success);
  flex-shrink: 0;
}
.gm-state-off {
  font-family: var(--font-tufte-sans);
  font-size: 0.8rem;
  color: var(--color-tufte-muted);
  flex-shrink: 0;
}

.gm-description {
  padding: 0.85rem 1.25rem 0;
  font-size: 1.02rem;
  line-height: 1.5;
  color: var(--color-tufte-secondary);
  font-style: italic;
}

.gm-section-label {
  font-family: var(--font-tufte-sans);
  font-size: 0.7rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  padding: 0 1.25rem 0.4rem;
  margin-top: 1.2rem;
}

.gm-group {
  padding: 0 1.25rem;
}

.gm-schedule {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem 0.4rem;
}

.gm-sl {
  display: inline-flex;
  border: 1px solid var(--color-tufte-border);
  padding: 0.1rem 0.5rem;
  font-family: var(--font-tufte-mono);
  font-size: 0.86rem;
  white-space: nowrap;
}

.gm-performers {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 1rem;
}

.gm-perf--m {
  color: var(--color-gender-men);
}
.gm-perf--f {
  color: var(--color-gender-women);
}

.gm-svcs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem 0.35rem;
}

.gm-svc {
  background: var(--color-surface-success);
  color: var(--color-tufte-success);
  font-family: var(--font-tufte-sans);
  font-size: 0.82rem;
  padding: 0.1rem 0.5rem;
}

.gm-empty-note {
  font-family: var(--font-tufte-sans);
  font-size: 0.86rem;
  color: var(--color-tufte-muted);
  font-style: italic;
}

.gm-fill {
  flex: 1;
}

.gm-danger-group {
  margin-top: 1.4rem;
}

.gm-danger-row {
  width: 100%;
  display: block;
  background: var(--color-tufte-bg);
  border: none;
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 0.85rem 1.25rem;
  text-align: center;
  font: inherit;
  font-size: 0.95rem;
  text-transform: uppercase;
  color: var(--color-tufte-danger);
  cursor: pointer;
}
.gm-danger-row:active {
  background: color-mix(in srgb, var(--color-tufte-danger) 5%, var(--color-tufte-bg));
}
.gm-danger-row:disabled {
  opacity: 0.5;
  cursor: default;
}

.gm-scroll-end {
  height: 2.5rem;
}
</style>
